import {
  type Kpi,
  KPIS,
  METRIC_TYPES,
  type MetricType,
  type TableKpi,
} from 'lumen/models/market-performance/data-item';
import {
  GROUP_COLUMNS_BY,
  segmentMetricType,
  SORT_ON,
  type SortOn,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';
import {
  type ColumnSettings,
  getVisibleKpis,
  getVisibleMetricTypes,
} from 'lumen/utils/market-snapshots/column-settings';

/**
 * Repoints an `orderBy` value (e.g. `sdly.revenue` or `-bi_forecast.adr`) from
 * one metric type to another, preserving the sub-metric and sort direction.
 *
 * Used when a swappable column (SDLY column, forecast) changes: a sort on the
 * old metric type would otherwise become orphaned (the API receives an
 * `order_by` for a metric no longer in `compare`). Returns the value unchanged
 * when it isn't sorted by `from` (including non-metric sorts like `name`).
 */
export function remapOrderByMetric(
  orderBy: string,
  from: string,
  to: string,
): string {
  const isDescending = orderBy.startsWith('-');
  const field = isDescending ? orderBy.slice(1) : orderBy;

  const separatorIndex = field.indexOf('.');
  if (separatorIndex === -1 || field.slice(0, separatorIndex) !== from) {
    return orderBy;
  }

  const remapped = `${to}${field.slice(separatorIndex)}`;
  return isDescending ? `-${remapped}` : remapped;
}

// Where a sort reads its value: the row's own totals or one of its segments.
const SOURCES = ['totals', 'transient', 'group_otb', 'group_blk'] as const;

type Source = (typeof SOURCES)[number];

// The API metrics a sort can name; the segment columns are not metrics of their own.
const BASE_METRIC_TYPES = METRIC_TYPES.filter(
  (metricType) =>
    !metricType.startsWith('transient') && !metricType.startsWith('group'),
);

const ORDER_BY_PATTERN = new RegExp(
  `^(-?)(${BASE_METRIC_TYPES.join('|')})\\.(${SOURCES.join('|')})\\.(${KPIS.join('|')})$`,
);

/**
 * Splits a `{metric}.{source}.{kpi}` orderBy (e.g. `sdly.totals.rooms` or
 * `sdly.transient.rooms`); null for anything else (e.g. `name`). `metricType`
 * is the column's: `transient:sdly` for a segment source.
 */
export function parseOrderBy(
  orderBy: string,
): { kpi: Kpi; metricType: MetricType } | null {
  const match = ORDER_BY_PATTERN.exec(orderBy);

  if (!match) {
    return null;
  }

  const base = match[2] as MetricType;
  const source = match[3] as Source;

  return {
    kpi: match[4] as Kpi,
    metricType:
      source === 'totals'
        ? base
        : segmentMetricType(
            source === 'transient' ? 'transient' : 'group',
            base,
          ),
  };
}

// The segment and KPI a sub-column of a segment column reads (see the table).
const SEGMENT_SOURCES: Record<TableKpi, { kpi: Kpi; source: Source }> = {
  adr: { kpi: 'adr', source: 'group_otb' },
  occupancy: { kpi: 'occupancy', source: 'group_otb' },
  otb_blk: { kpi: 'rooms', source: 'group_otb' },
  rev_blk: { kpi: 'revenue', source: 'group_blk' },
  revenue: { kpi: 'revenue', source: 'group_otb' },
  revpar: { kpi: 'revpar', source: 'group_otb' },
  rooms: { kpi: 'rooms', source: 'group_otb' },
};

/** The semantic orderBy of a sub-column, the inverse of `parseOrderBy`. */
export function orderByKey(metricType: MetricType, kpi: TableKpi): string {
  const [segment, base = 'otb'] = metricType.split(':');

  if (segment === 'transient') {
    return `${base}.transient.${kpi}`;
  }

  if (segment === 'group') {
    const { kpi: groupKpi, source } = SEGMENT_SOURCES[kpi];

    return `${base}.${source}.${groupKpi}`;
  }

  return `${metricType}.totals.${kpi}`;
}

/** True when `orderBy` sorts on a column the table currently hides; `name` never is. */
export function isSortedColumnHidden(
  orderBy: string,
  {
    isKpiMode,
    visibleKpis,
    visibleMetricTypes,
  }: {
    isKpiMode: boolean;
    visibleKpis: readonly Kpi[];
    visibleMetricTypes: readonly MetricType[];
  },
): boolean {
  const sorted = parseOrderBy(orderBy);

  return (
    sorted !== null &&
    (isKpiMode
      ? !visibleKpis.includes(sorted.kpi)
      : !visibleMetricTypes.includes(sorted.metricType))
  );
}

/** A bookmarked or stale sort on a column the given layout hides falls back to `name`. */
export function getEffectiveOrderBy(
  orderBy: string,
  {
    columnSettings,
    fixed,
    kpiSettings,
    layout,
    metrics,
  }: {
    columnSettings: ColumnSettings;
    fixed?: MetricType;
    kpiSettings: ColumnSettings<Kpi>;
    layout: TableLayoutPreferences;
    metrics: Record<string, MetricType>;
  },
): string {
  const isHidden = isSortedColumnHidden(orderBy, {
    isKpiMode: layout.groupColumnsBy === GROUP_COLUMNS_BY.KPIS,
    visibleKpis: getVisibleKpis(kpiSettings),
    visibleMetricTypes: getVisibleMetricTypes(
      columnSettings,
      layout,
      metrics,
      fixed,
    ),
  });

  return isHidden ? 'name' : orderBy;
}

/** URL keeps `{metric}.{source}.{kpi}`; returns `{metric}.{source}.variance.{kpi}.{percent|absolute}` when sorting on variance (not OTB), else unchanged. */
export function buildApiOrderBy(
  orderBy: string,
  sortOn: SortOn,
  varianceType: Variance,
): string {
  const match = ORDER_BY_PATTERN.exec(orderBy);
  if (!match || sortOn === SORT_ON.ACTUALS || match[2] === 'otb') {
    return orderBy;
  }

  const [, direction, metricType, source, kpi] = match;
  const suffix = varianceType === VARIANCES.ABSOLUTE ? 'absolute' : 'percent';

  return `${direction}${metricType}.${source}.variance.${kpi}.${suffix}`;
}

/** Whether a variance change alters the API order_by for `orderBy` (only when sorting on variance, OTB exempt). */
export function apiOrderByChanged(
  orderBy: string,
  sortOn: SortOn,
  from: Variance,
  to: Variance,
): boolean {
  return (
    buildApiOrderBy(orderBy, sortOn, from) !==
    buildApiOrderBy(orderBy, sortOn, to)
  );
}
